import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { ChevronDown, X, Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Music2, Mic2, Share2, ListMusic, ListPlus, Plus, GripVertical, Trash2, Eraser, ListPlus as SaveIcon } from "lucide-react";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { musicClient } from "@/lib/musicApi";
import LikeButton from "@/components/music/LikeButton";
import SaveButton from "@/components/music/SaveButton";
import { AddToPlaylistModal } from "@/components/music/AddToPlaylistModal";
import OnboardingHint from "@/components/onboarding/OnboardingHint";
import { useHints } from "@/contexts/HintsContext";
import { useContentTranslation } from "@/lib/useContentTranslation";
import { TranslateToggle } from "@/components/common/TranslateToggle";
import { MembersOnlySheet } from "@/components/common/MembersOnlySheet";
import { LyricsPanel } from "@/components/player/LyricsPanel";
import { useToast } from "@/contexts/ToastContext";
import { shareContent } from "@/lib/shareUtil";
import { TipWidget } from "@/components/tips/TipWidget";
import { TileImage } from "@/components/common/Skeleton";

const fmtTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  s = Math.floor(s);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const Scrubber = ({ progress, duration, onSeek }) => (
  <div className="w-full">
    <input
      type="range" min={0} max={duration || 0} value={progress || 0} step={0.1}
      onChange={(e) => onSeek(parseFloat(e.target.value))}
      data-testid="player-scrubber"
      className="w-full accent-[color:var(--accent-primary)] cursor-pointer"
      style={{ accentColor: "var(--accent-primary)" }}
    />
    <div className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
      <span>{fmtTime(progress)}</span>
      <span>{fmtTime(duration)}</span>
    </div>
  </div>
);

const CoverBox = ({ src, size, radius = 16 }) => (
  <div style={{ width: size, height: size, borderRadius: radius, overflow: "hidden", background: "var(--bg-subtle)", flexShrink: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
    {src && String(src).trim() ? <TileImage src={src} className="w-full h-full object-cover" /> : <Music2 style={{ color: "var(--text-secondary)" }} />}
  </div>
);

// One draggable row in the Queue view.
const QueueRow = ({ track, isCurrent, onRemove, onPlay }) => {
  const controls = useDragControls();
  const { t } = useTranslation();
  return (
    <Reorder.Item value={track} dragListener={false} dragControls={controls}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl" data-testid={`queue-row-${track.id}`}
      style={{ background: isCurrent ? "var(--bg-subtle)" : "transparent", listStyle: "none" }}>
      <button onPointerDown={(e) => controls.start(e)} data-testid={`queue-drag-${track.id}`} aria-label={t("player.dragReorder")} style={{ color: "var(--text-secondary)", cursor: "grab", touchAction: "none" }}><GripVertical size={18} /></button>
      <CoverBox src={track.cover} size={40} radius={8} />
      <button onClick={() => onPlay(track)} className="min-w-0 flex-1 text-left">
        <p className="text-sm font-medium truncate" style={{ color: isCurrent ? "var(--accent-primary)" : "var(--text-primary)" }}>{track.title}</p>
        <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{isCurrent ? t("player.nowPlaying") : (track.featuring ? `feat. ${track.featuring}` : "SAMTWO")}</p>
      </button>
      <button onClick={() => onRemove(track.id)} data-testid={`queue-remove-${track.id}`} aria-label={t("player.removeFromQueue")} style={{ color: "var(--text-secondary)" }}><Trash2 size={16} /></button>
    </Reorder.Item>
  );
};

const QueueView = ({ p, onClose, canSave, onSavePlaylist, rail, onRailPlay, onRailQueue }) => {
  const { t } = useTranslation();
  return (
  <motion.div key="queue" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
    transition={{ type: "spring", stiffness: 260, damping: 30 }}
    data-testid="player-queue-view" className="fixed inset-0 z-[10000] flex flex-col px-6 py-6" style={{ background: "var(--bg-primary)" }}>
    <div className="w-full max-w-md mx-auto flex items-center justify-between mb-5">
      <h2 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>{t("player.upNext")}</h2>
      <button onClick={onClose} data-testid="queue-close" aria-label={t("player.closeQueue")} style={{ color: "var(--text-primary)" }}><ChevronDown size={26} /></button>
    </div>
    <div className="w-full max-w-md mx-auto flex items-center gap-2 mb-4">
      <button onClick={p.clearUpNext} disabled={p.queue.length <= 1} data-testid="queue-clear-next"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium disabled:opacity-40"
        style={{ background: "var(--bg-subtle)", color: "var(--text-primary)" }}>
        <Eraser size={14} /> {t("player.clearUpNext")}
      </button>
      {canSave && (
        <button onClick={onSavePlaylist} disabled={!p.queue.length} data-testid="queue-save-playlist"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium disabled:opacity-40"
          style={{ background: "var(--bg-subtle)", color: "var(--text-primary)" }}>
          <SaveIcon size={14} /> {t("player.saveAsPlaylist")}
        </button>
      )}
    </div>
    <div className="w-full max-w-md mx-auto flex-1 overflow-y-auto">
      {p.queue.length ? (
        <Reorder.Group axis="y" values={p.queue} onReorder={p.reorderQueue} className="space-y-1" style={{ padding: 0, margin: 0 }}>
          {p.queue.map((tr) => (
            <QueueRow key={tr.id} track={tr} isCurrent={p.current && p.current.id === tr.id}
              onRemove={p.removeFromQueue}
              onPlay={(track) => p.jumpToQueueIndex(p.queue.findIndex((x) => x.id === track.id))} />
          ))}
        </Reorder.Group>
      ) : (
        <p className="text-sm text-center py-10" style={{ color: "var(--text-secondary)" }}>{t("player.queueEmpty")}</p>
      )}

      {rail && rail.length > 0 && (
        <div className="mt-8" data-testid="player-rail">
          <h3 className="text-sm font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("player.railTitle")}</h3>
          <div className="space-y-1">
            {rail.map((tr) => (
              <div key={tr.id} data-testid={`rail-row-${tr.id}`} className="flex items-center gap-3 px-3 py-2 rounded-xl">
                <button onClick={() => onRailPlay(tr)} data-testid={`rail-play-${tr.id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                  <CoverBox src={tr.cover} size={40} radius={8} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{tr.title}</p>
                    <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{tr.featuring ? `feat. ${tr.featuring}` : "SAMTWO"}</p>
                  </div>
                </button>
                <button onClick={() => onRailQueue(tr)} data-testid={`rail-add-queue-${tr.id}`} aria-label={t("music.menuAddQueue")} style={{ color: "var(--text-secondary)" }} className="p-1.5"><Plus size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </motion.div>
  );
};

export const AudioPlayer = () => {
  const p = useAudioPlayer();
  const { t } = useTranslation();
  const location = useLocation();
  const { markSeen } = useHints();
  const toast = useToast();
  const { isSubscriber } = usePlatformAuth();
  const [showQueue, setShowQueue] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const [showLyricsUpsell, setShowLyricsUpsell] = useState(false);
  const [showAddUpsell, setShowAddUpsell] = useState(false);
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [rail, setRail] = useState([]);
  const [barHidden, setBarHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const fetchTrackT = useCallback(async (lang) => {
    const { data } = await musicClient.get(`/tracks/${p.current?.id}/translate`, { params: { lang } });
    return { title: data.title, description: data.description };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.current?.id]);
  const trackT = useContentTranslation({ title: p.current?.title || "" }, fetchTrackT, { auto: true, key: p.current?.id });

  // Inline Mini only lives on the Music tab; leaving switches to the Global Mini-bar.
  useEffect(() => {
    if (p.playerUI === "inline" && location.pathname !== "/music") p.setPlayerUI("global");
  }, [location.pathname, p]);

  // Build the "You might also like" rail when the queue view opens: same-artist
  // catalog tracks excluding the current track and anything already queued.
  useEffect(() => {
    if (!showQueue) return;
    let alive = true;
    musicClient.get("/tracks").then((r) => {
      if (!alive) return;
      const inQueue = new Set((p.queue || []).map((qt) => qt.id));
      if (p.current) inQueue.add(p.current.id);
      setRail((r.data || []).filter((c) => !inQueue.has(c.id) && !c.locked).slice(0, 8));
    }).catch(() => setRail([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQueue, p.queue.length, p.current?.id]);

  const onSavePlaylist = async () => {
    const ids = (p.queue || []).map((qt) => qt.id);
    if (!ids.length) return;
    const title = p.current ? `${p.current.title} mix` : t("player.myPlaylist");
    try {
      await musicClient.post("/playlists", { title, track_ids: ids });
      toast.success(t("player.savedAs", { title }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("player.saveError"));
    }
  };

  if (!p.current) return null;
  const cur = p.current;

  return (
    <>
      <AnimatePresence>
        {p.playerUI === "fullscreen" && (
          <motion.div
            key="fs" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            data-testid="player-fullscreen"
            className="fixed inset-0 z-[9999] flex flex-col items-center px-6 py-6"
            style={{ background: "var(--bg-primary)" }}
          >
            <div className="w-full max-w-md flex items-center justify-between mb-8">
              <div className="relative" onClickCapture={() => markSeen("player_collapse")}>
                <OnboardingHint hintKey="player_collapse" text={t("player.hintCollapse")} className="left-0 top-full mt-1" />
                <button onClick={() => p.setPlayerUI("inline")} data-testid="player-collapse-inline" aria-label={t("player.minimize")} style={{ color: "var(--text-primary)" }}><ChevronDown size={26} /></button>
              </div>
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>{t("player.nowPlaying")}</span>
              <button onClick={() => p.setPlayerUI("global")} data-testid="player-collapse-global" aria-label={t("music.close")} style={{ color: "var(--text-primary)" }}><X size={24} /></button>
            </div>
            <div className="w-full max-w-md flex-1 flex flex-col justify-center">
              <div className="flex justify-center mb-8"><CoverBox src={cur.cover} size={280} radius={24} /></div>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold truncate" data-testid="player-title" style={{ color: "var(--text-primary)" }}>{trackT.values.title}</h2>
                  <p className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{cur.featuring ? `feat. ${cur.featuring}` : "SAMTWO"}</p>
                  <TranslateToggle testid="player-translate-toggle" translating={trackT.translating} hasTranslation={trackT.hasTranslation} showOriginal={trackT.showOriginal} onToggle={trackT.onToggle} className="mt-1" style={{ color: "var(--accent-primary)" }} />
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <LikeButton trackId={cur.id} initialCount={cur.like_count} initialLiked={cur.liked} size={22} showCount={false} />
                  <SaveButton trackId={cur.id} initialSaved={cur.saved} size={22} />
                  <button onClick={() => { if (!isSubscriber) { setShowAddUpsell(true); return; } setShowAddPlaylist(true); }} data-testid="player-add-playlist" aria-label={t("music.menuAddPlaylist")} style={{ color: "var(--text-secondary)" }}>
                    <ListPlus size={22} />
                  </button>
                  <button onClick={() => shareContent("track", cur.id, cur.title, toast)} data-testid="player-share" aria-label={t("music.share")} style={{ color: "var(--text-secondary)" }}>
                    <Share2 size={22} />
                  </button>
                  <div data-testid="player-tip"><TipWidget compact sourceType="track" sourceId={cur.id} /></div>
                </div>
              </div>
              <div className="mb-6"><Scrubber progress={p.progress} duration={p.duration} onSeek={p.seek} /></div>
              <div className="flex items-center justify-between">
                <button onClick={p.toggleShuffle} data-testid="player-shuffle" aria-label={t("detail.shuffle")} style={{ color: p.shuffle ? "var(--accent-primary)" : "var(--text-secondary)" }}><Shuffle size={20} /></button>
                <div className="flex items-center gap-6">
                  <button onClick={p.prev} data-testid="player-prev" aria-label={t("player.previous")} style={{ color: "var(--text-primary)" }}><SkipBack size={28} /></button>
                  <button onClick={p.togglePlay} data-testid="player-toggle" aria-label={p.isPlaying ? t("player.pause") : t("detail.play")} className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--accent-primary)", color: "#fff" }}>
                    {p.isPlaying ? <Pause size={28} /> : <Play size={28} style={{ marginLeft: 3 }} />}
                  </button>
                  <button onClick={p.next} data-testid="player-next" aria-label={t("player.next")} style={{ color: "var(--text-primary)" }}><SkipForward size={28} /></button>
                </div>
                <button onClick={p.toggleRepeat} data-testid="player-repeat" aria-label={t("player.repeat")} style={{ color: p.repeat ? "var(--accent-primary)" : "var(--text-secondary)" }}><Repeat size={20} /></button>
              </div>
              <div className="flex justify-center gap-3 mt-6">
                <button onClick={() => setShowQueue(true)} data-testid="player-queue-btn" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ background: "var(--bg-subtle)", color: "var(--text-primary)" }}>
                  <ListMusic size={16} /> {t("player.queue")}{p.queue.length ? ` (${p.queue.length})` : ""}
                </button>
                <button onClick={() => { if (isSubscriber) setShowLyrics(true); else setShowLyricsUpsell(true); }} data-testid="player-lyrics-btn" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ background: "var(--bg-subtle)", color: "var(--text-primary)" }}>
                  <Mic2 size={16} /> {t("lyrics.title")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLyrics && p.current && (
          <LyricsPanel track={p.current} progress={p.progress} onSeek={p.seek} onClose={() => setShowLyrics(false)} />
        )}
      </AnimatePresence>
      <MembersOnlySheet open={showLyricsUpsell} onClose={() => setShowLyricsUpsell(false)} title={t("lyrics.membersTitle")} body={t("lyrics.membersBody")} />
      <MembersOnlySheet open={showAddUpsell} onClose={() => setShowAddUpsell(false)} />

      <AnimatePresence>
        {p.playerUI === "fullscreen" && showQueue && (
          <QueueView p={p} onClose={() => setShowQueue(false)}
            canSave={isSubscriber} onSavePlaylist={onSavePlaylist}
            rail={rail} onRailPlay={(track) => p.playNow(track)} onRailQueue={(track) => p.addToQueue(track)} />
        )}
      </AnimatePresence>

      {showAddPlaylist && <AddToPlaylistModal trackId={cur.id} onClose={() => setShowAddPlaylist(false)} />}

      <AnimatePresence>
        {p.playerUI === "global" && (
          <motion.div
            key="mini" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            data-testid="player-global-minibar"
            className="fixed left-0 right-0 z-[9999]"
            style={{
              background: "var(--bg-surface)", borderTop: "1px solid var(--border-color)",
              // Mobile: sit ABOVE the bottom nav (nav is at safe-area+12px, ~48px tall) so
              // it can never cover the tab bar. Desktop nav is a left rail, stay at bottom.
              bottom: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 74px)" : 0,
            }}
          >
            {/* Mobile-only transparent Hide/Show control, just above the docked bar */}
            {isMobile && (
              <div className="absolute left-0 right-0 -top-7 flex justify-center pointer-events-none">
                <button
                  onClick={() => setBarHidden((h) => !h)}
                  data-testid="minibar-hide-toggle"
                  className="pointer-events-auto text-xs font-semibold px-4 py-1 rounded-t-lg"
                  style={{ background: "transparent", color: "var(--text-secondary)" }}
                >
                  {barHidden ? t("player.showBar") : t("player.hideBar")}
                </button>
              </div>
            )}
            {!(isMobile && barHidden) && (
              <>
                <div style={{ height: 3, background: "var(--bg-subtle)" }}>
                  <div style={{ height: "100%", width: `${p.duration ? (p.progress / p.duration) * 100 : 0}%`, background: "var(--accent-primary)" }} />
                </div>
                <div className="flex items-center gap-3 px-4 py-2 max-w-5xl mx-auto">
                  <button onClick={() => p.setPlayerUI("fullscreen")} data-testid="minibar-restore" className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <CoverBox src={cur.cover} size={44} radius={8} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{cur.title}</p>
                      <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{cur.featuring ? `feat. ${cur.featuring}` : "SAMTWO"}</p>
                    </div>
                  </button>
                  <button onClick={p.prev} data-testid="minibar-prev" aria-label={t("player.previous")} className="flex-shrink-0" style={{ color: "var(--text-primary)" }}><SkipBack size={18} /></button>
                  <button onClick={p.togglePlay} data-testid="minibar-toggle" aria-label={p.isPlaying ? t("player.pause") : t("detail.play")} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--accent-primary)", color: "#fff" }}>
                    {p.isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
                  </button>
                  <button onClick={p.next} data-testid="minibar-next" aria-label={t("player.next")} className="flex-shrink-0" style={{ color: "var(--text-primary)" }}><SkipForward size={18} /></button>
                  <button onClick={p.dismiss} data-testid="minibar-close" aria-label={t("player.dismiss")} style={{ color: "var(--text-secondary)" }}><X size={18} /></button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AudioPlayer;
